import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import { Input } from '../../design-system/Input';
import {
  Layers,
  Plus,
  Search,
  Globe,
  Lock,
  ArrowRight,
  TrendingUp,
  FolderArchive,
  RotateCcw,
  Trash2
} from 'lucide-react';

export const ProjectsDirectoryPage = ({ setCurrentView, setCurrentProjectView }) => {
  const {
    projects,
    activeWorkspace,
    setActiveProjectId,
    allTasks,
    setIsCreateProjectModalOpen,
    archiveProject,
    restoreProject,
    deleteProject
  } = useWorkspace();

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active'); // 'Active' or 'Archived'

  const categories = ['All', 'Software Engineering', 'Game Development', 'Hardware Engineering', 'Scientific Research', 'General'];

  // Filter projects by workspace, status, category, and search query
  const workspaceProjects = projects.filter((p) => p.workspaceId === activeWorkspace?.id);

  const filteredProjects = workspaceProjects.filter((p) => {
    const matchesStatus = (p.status || 'Active') === statusFilter;
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.key.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesCategory && matchesSearch;
  });

  const getProjectStats = (prjId) => {
    const prjTasks = allTasks.filter((t) => t.projectId === prjId);
    const completed = prjTasks.filter((t) => t.status === 'done').length;
    const progress = prjTasks.length > 0 ? Math.round((completed / prjTasks.length) * 100) : 0;
    return { total: prjTasks.length, completed, progress };
  };

  const handleOpenProject = (prjId) => {
    setActiveProjectId(prjId);
    setCurrentView('project_view');
    if (setCurrentProjectView) {
      setCurrentProjectView('board');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">Directory</Badge>
            <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Projects in {activeWorkspace?.name}</h1>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Browse active project workspaces, track velocity, and launch discipline views
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            <button
              onClick={() => setStatusFilter('Active')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: statusFilter === 'Active' ? 'var(--surface-card)' : 'transparent',
                boxShadow: statusFilter === 'Active' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: statusFilter === 'Active' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Active ({workspaceProjects.filter((p) => (p.status || 'Active') === 'Active').length})
            </button>
            <button
              onClick={() => setStatusFilter('Archived')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: statusFilter === 'Archived' ? 'var(--surface-card)' : 'transparent',
                boxShadow: statusFilter === 'Archived' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: statusFilter === 'Archived' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Archived ({workspaceProjects.filter((p) => p.status === 'Archived').length})
            </button>
          </div>

          <Button
            variant="primary"
            size="md"
            icon={<Plus size={16} />}
            onClick={() => setIsCreateProjectModalOpen(true)}
          >
            New Project
          </Button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ maxWidth: '320px', width: '100%' }}>
          <Input
            placeholder="Search projects by name, key, or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<Search size={15} />}
          />
        </div>

        {/* Category Pills */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: '5px 12px',
                borderRadius: '999px',
                backgroundColor: selectedCategory === cat ? 'var(--surface-recessed)' : 'transparent',
                boxShadow: selectedCategory === cat ? 'var(--shadow-recessed-sm)' : 'none',
                border: selectedCategory === cat ? '1px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                color: selectedCategory === cat ? 'var(--accent-teal)' : 'var(--text-secondary)',
                fontSize: '12px',
                fontWeight: selectedCategory === cat ? '700' : '500',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card padding="48px">
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Layers size={36} color="var(--accent-teal)" />
            <h3 style={{ fontSize: '18px', fontWeight: '800' }}>No Projects Found</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '380px' }}>
              No projects matching your search criteria. Create your first project to organize work streams.
            </p>
            <Button
              variant="primary"
              size="md"
              icon={<Plus size={16} />}
              onClick={() => setIsCreateProjectModalOpen(true)}
            >
              Create Project
            </Button>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '18px' }}>
          {filteredProjects.map((prj) => {
            const stats = getProjectStats(prj.id);
            const isArchived = prj.status === 'Archived';

            return (
              <Card
                key={prj.id}
                padding="20px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '16px',
                  position: 'relative'
                }}
              >
                {/* Top Row: Key & Visibility & Category */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '800',
                          padding: '3px 7px',
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: 'rgba(35, 190, 179, 0.15)',
                          color: 'var(--accent-teal)',
                          border: '1px solid rgba(35, 190, 179, 0.3)'
                        }}
                      >
                        {prj.key}
                      </span>
                      <Badge variant="neutral" size="xs">
                        {prj.category || 'General'}
                      </Badge>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {prj.visibility === 'Private' ? (
                        <span title="Private Project" style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                          <Lock size={13} />
                        </span>
                      ) : (
                        <span title="Public Project" style={{ color: 'var(--accent-teal)', display: 'flex', alignItems: 'center' }}>
                          <Globe size={13} />
                        </span>
                      )}
                    </div>
                  </div>

                  <h3
                    onClick={() => handleOpenProject(prj.id)}
                    style={{
                      fontSize: '17px',
                      fontWeight: '800',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      marginBottom: '6px'
                    }}
                  >
                    {prj.name}
                  </h3>

                  <p
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.5',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '36px'
                    }}
                  >
                    {prj.description || 'No description provided.'}
                  </p>
                </div>

                {/* Progress Meter */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '5px' }}>
                    <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TrendingUp size={12} /> {stats.completed} of {stats.total} tasks done
                    </span>
                    <span style={{ fontWeight: '700', color: 'var(--accent-teal)' }}>{stats.progress}%</span>
                  </div>
                  <div
                    style={{
                      height: '6px',
                      borderRadius: '999px',
                      backgroundColor: 'var(--surface-recessed)',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${stats.progress}%`,
                        backgroundColor: 'var(--accent-teal)',
                        borderRadius: '999px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                </div>

                {/* Footer Row: Lead & Actions */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-divider)'
                  }}
                >
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Lead: <strong>{prj.lead || 'Rohith Prince'}</strong>
                  </span>

                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {isArchived ? (
                      <>
                        <button
                          onClick={() => restoreProject(prj.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--accent-teal)',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Restore Project"
                        >
                          <RotateCcw size={15} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Permanently delete ${prj.name}?`)) {
                              deleteProject(prj.id);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-danger)',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Delete Project"
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => archiveProject(prj.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Archive Project"
                        >
                          <FolderArchive size={15} />
                        </button>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<ArrowRight size={14} />}
                          onClick={() => handleOpenProject(prj.id)}
                        >
                          Open
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
